import { useStore } from '@nanostores/react'

import Cell from '@/components/atoms/Cell'
import SectionHeader from '@/components/atoms/SectionHeader'
import { useScopedI18n } from '@/locales/client'
import useCartStore from '@/stores/CartStore'
import { $country } from '@/stores/Country'
import { useHubStore } from '@/stores/HubStore'
import { useMobileLayoutStore } from '@/stores/MobileLayoutStore'
import { $promotion } from '@/stores/PromotionStore'
import { cn } from '@/util/cn'
import { calculateDiscount, calculateFinalPrice, getDiscountPercent, priceFormatter } from '@/util/formatter'

function PriceSection() {
  const { isMobile, activeMobileTab } = useMobileLayoutStore()
  const t = useScopedI18n('priceSection')
  const hubs = useHubStore(state => state.hubs)
  const totalPerHub = useCartStore(store => store.totalPerHub())
  const country = useStore($country)
  const promotion = useStore($promotion)

  if (!country) return <></>

  const discountPercent = getDiscountPercent()
  const hasDiscount = discountPercent > 0

  return (
    <section className={cn(
      'grid grid-cols-4 gap-1 tablet:grid-cols-5',
      isMobile && activeMobileTab === 'deluxe' && 'grid-cols-4',
    )}
    >
      {hasDiscount ? (
        <>
          {/* Labels column - with promotion */}
          <div className="col-span-2 flex flex-col justify-center gap-1 rounded-lg bg-white p-4">
            <p className="text-base font-semibold text-rsf-black">{t('subtotal')}</p>
            <p className="text-sm text-rsf-black">{promotion?.labelSubtotal || t('discount')}</p>
            <div className="my-2 h-px w-full bg-rsf-grey-light" />
            <p className="text-base font-bold text-rsf-black">{t('total')}</p>
          </div>

          {hubs.map(hub => (
            <Cell
              key={hub.id}
              className={cn(
                'col-span-1',
                isMobile && activeMobileTab === 'deluxe' && 'col-span-2',
              )}
            >
              <div className="flex flex-col items-center gap-1">
                <p className="text-center text-base leading-tight text-rsf-black">
                  {priceFormatter((totalPerHub[hub.id] ?? 0), hub.pricing.currency)}
                </p>
                <p className="text-center text-base leading-tight text-rsf-green">
                  -{priceFormatter(calculateDiscount(totalPerHub[hub.id] ?? 0), hub.pricing.currency)}
                </p>
                <div className="my-2 h-px w-full bg-rsf-grey-light" />
                <p className="text-center md:text-lg text-sm md:mt-0 mt-2 font-bold leading-tight text-rsf-black">
                  {priceFormatter(calculateFinalPrice(totalPerHub[hub.id] ?? 0), hub.pricing.currency)}
                </p>
              </div>
            </Cell>
          ))}
        </>
      ) : (
        <>
          {/* Original layout - no promotion */}
          <SectionHeader title={t('total')} className="col-span-2 grid grid-cols-2 tablet:grid-cols-2" />

          {hubs.map(hub => (
            <Cell
              key={hub.id}
              className={cn(
                'col-span-1',
                isMobile && activeMobileTab === 'deluxe' && 'col-span-2',
              )}
            >
              <p className="text-center md:text-lg text-sm md:mt-0 mt-2 font-bold leading-tight text-rsf-black">
                {priceFormatter((totalPerHub[hub.id] ?? 0), hub.pricing.currency)}
              </p>
            </Cell>
          ))}
        </>
      )}
    </section>
  )
}

export default PriceSection
